import React, { useContext } from "react";
import {
  Typography,
  Box,
  Stack,
  Icon,
  Divider,
  Link,
  Button,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { CommentsList } from "../CommentsList.js";
import User1 from "src/assets/users/user1.png";
import User2 from "src/assets/users/user2.png";
import User3 from "src/assets/users/user3.png";
import User4 from "src/assets/users/user4.png";
import { TimeSaveBlock } from "./TimeSaveBlock.js";
import FAQAccordion from "../../instructions/FAQAccordian.js";
import { PlanComparisonTable } from "src/components/upgrades/PlanComparisonTable.js";
import { consumerPlanData } from "./ConsumerPlanData.js";
import BenefitsCarousel from "../BenefitsCarousel.js";
import CreatorFundExplainer from "../../explainers/CreatorFundExplainer.js";
import { AuthContext } from "src/utils/AuthContext.js";
import { useNavigate } from "react-router-dom";
import { getPageRoute } from "src/services";

const ConsumerUpgradeContent = ({ handleUpgradeClick }) => {
  const theme = useTheme();
  const user = useContext(AuthContext).user;
  const isLifeTimeMember = user?.lifetimeMember;
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate(getPageRoute("home", "HOME"));
  };

  const faqData = [
    {
      question: "What is Stanza+?",
      answer: (
        <>
          Stanza+ is our premium membership that gives you access to exclusive
          calendars, unlimited subscriptions, priority updates, and personalized
          features like smart recommendations. Best of all, you'll know exactly
          when your calendar was last updated.
          <br />
          <br />
          With over 60,000 calendars in our library, there's something for
          everyone.
        </>
      ),
    },
    {
      question: "How many calendars can I add as a free user?",
      answer: (
        <>
          You're limited to 2 free calendars. You won't have access to calendars
          that are exclusive to Stanza+.
        </>
      ),
    },
    {
      question: "Can I use Stanza+ across all my devices?",
      answer: (
        <>
          Yes, one Stanza+ subscription allows you to access Stanza+ and your
          subscribed calendars on all your devices.
        </>
      ),
    },
    {
      question: "How do I make the most of the free trial?",
      answer: (
        <>
          The free trial is designed to help you understand whether or not
          Stanza can help you keep up with the things you care about, to
          ultimately spend your time more meaningfully. <br /> <br /> Add as
          many calendars as you like, organize them using groups and get faster
          updates.
        </>
      ),
    },
    {
      question: "Will I be notified before my free trial ends?",
      answer:
        "Yes, you will receive an email from Stripe notifying you of what you will be billed for and when.",
    },
    {
      question: "How can I cancel my free trial?",
      answer: `After you sign up for the free trial, you'll see 'Your Plan' in the drop down menu in the top right corner. Click on 'Your Plan' and then 'Cancel Subscription' on the next screen. It's as easy as as two clicks.`,
    },
    {
      question: "My payment information isn't being accepted.",
      answer: (
        <>
          If you're experiencing issues with a valid card not being accepted,
          please send us an email at{" "}
          <Link href="mailto:founders@stanza.co">founders@stanza.co</Link> with
          more details on the error. We'll look into it asap.
        </>
      ),
    },
  ];

  return (
    <>
      {/* Benefits Carousel - First Section */}
      <BenefitsCarousel
        variant="dark"
        onUpgradeClick={handleUpgradeClick}
        sx={{
          margin: "0 auto",
          mt: 5,
          mb: 4,
        }}
      />
      <Divider sx={{ my: 2.5 }} />
      <Icon
        baseClassName="material-symbols-outlined"
        sx={{
          display: "block",
          margin: "0 auto",
          color: `${theme.palette.icon.primary}`,
          verticalAlign: "middle",
          fontSize: "3rem",
          mt: 5,
          mb: 1,
          fontWeight: 300,
        }}
      >
        redeem
      </Icon>
      <Typography
        variant="title"
        sx={{ mb: 3, display: "grid", justifyItems: "center" }}
      >
        {!user
          ? "Try Stanza+ for free for 7 days"
          : !isLifeTimeMember
          ? "Your first week's on us"
          : "Current Plan: Lifetime Membership"}
      </Typography>
      {!user && (
        <Typography variant="subtitle2" sx={{ mt: 1, mb: 3 }}>
          Then just $2.50/month (billed annually)
        </Typography>
      )}
      <Stack
        sx={{
          margin: "auto",
          maxWidth: "360px",
          textAlign: "center",
          gap: 1,
          mt: 3,
        }}
      >
        <PlanComparisonTable sx={{ mb: 3 }} data={consumerPlanData} />
        <Button
          data-testid="consumer-upgrade-button"
          variant="contained"
          color="primary"
          onClick={handleUpgradeClick}
        >
          {!isLifeTimeMember ? "Start Your Trial" : "Go home"}
        </Button>
        {!isLifeTimeMember && (
          <Typography variant="subtitle2" sx={{ py: 0.5, margin: "auto" }}>
            Billed $0 today. No commitment, cancel anytime.
          </Typography>
        )}

        <CommentsList
          comments={[
            {
              text: (
                <>
                  Calendars can be so clunky. Stanza makes it{" "}
                  <b>ridiculously easy to subscribe</b>. I'm up and running in
                  seconds.
                </>
              ),
              src: User1,
            },
            {
              text: (
                <>
                  What I love most? Stanza tells me{" "}
                  <b>
                    when my calendar was last updated with a timestamp in my
                    events
                  </b>
                  . No more guessing if a game or show time has changed—I know
                  I'm always in the loop.
                </>
              ),
              src: User2,
            },
          ]}
        />

        <Divider sx={{ my: 4 }} />

        {/* Vision Section */}
        <CreatorFundExplainer variant="dark" />

        <Divider sx={{ my: 4 }} />

        <Icon
          baseClassName="material-symbols-outlined"
          sx={{
            display: "block",
            margin: "0 auto",
            color: `${theme.palette.icon.primary}`,
            verticalAlign: "middle",
            fontSize: "3rem",
            mb: 1,
            fontWeight: 300,
          }}
        >
          live_help
        </Icon>

        <Typography variant="title" sx={{ mb: 2 }}>
          FAQs
        </Typography>
        <FAQAccordion faqData={faqData} />
        <Button
          size="grown"
          variant="outlined"
          onClick={() =>
            window.open(
              "https://spotlight.stanzacal.com/for-stanza-plus-members/"
            )
          }
          sx={{ height: "auto", mb: 1, width: "100%" }}
        >
          See all Stanza+ FAQs
        </Button>

        <Divider sx={{ my: 2.5 }} />

        <Icon
          baseClassName="material-symbols-outlined"
          sx={{
            display: "block",
            margin: "0 auto",
            color: `${theme.palette.icon.primary}`,
            verticalAlign: "middle",
            fontSize: "3rem",
            mb: 1,
            fontWeight: 300,
          }}
        >
          timelapse
        </Icon>
        <Typography variant="title" sx={{ width: "80%", margin: "0 auto" }}>
          How much is your time worth to you?
        </Typography>
        <CommentsList
          comments={[
            {
              text: (
                <>
                  If it wasn't for Stanza, I'd be spending{" "}
                  <b>hours manually adding</b> all my teams' games into my
                  calendar. Now it just <b>updates automatically</b> and I know
                  I won't miss out!
                </>
              ),
              src: User1,
            },
          ]}
        />
        <TimeSaveBlock
          handleGoHome={handleGoHome}
          isLifeTimeMember={isLifeTimeMember}
          handleButtonClick={handleUpgradeClick}
        />

        <Divider sx={{ my: 2.5 }} />

        <Stack
          spacing={1}
          direction="row"
          sx={{ display: "block", margin: "0 auto", mb: 1 }}
        >
          <Icon
            baseClassName="material-symbols-outlined"
            sx={{
              color: `${theme.palette.icon.primary}`,
              verticalAlign: "middle",
              fontSize: "3rem",
              fontWeight: 300,
            }}
          >
            favorite_border
          </Icon>
          <Icon
            baseClassName="material-symbols-outlined"
            sx={{
              color: `${theme.palette.icon.primary}`,
              verticalAlign: "middle",
              fontSize: "3rem",
              fontWeight: 300,
            }}
          >
            favorite_border
          </Icon>
          <Icon
            baseClassName="material-symbols-outlined"
            sx={{
              color: `${theme.palette.icon.primary}`,
              verticalAlign: "middle",
              fontSize: "3rem",
              fontWeight: 300,
            }}
          >
            favorite_border
          </Icon>
        </Stack>
        <Typography variant="title">All the love for Stanza+</Typography>
        <CommentsList
          comments={[
            {
              text: (
                <>
                  The ability to <b>group all my sports calendars</b> is
                  AMAZING!!! I just have one calendar I add to my Google
                  Calendar and don't have to deal with the clutter of a giant
                  list in my sidebar.
                </>
              ),
              src: User1,
            },
            {
              text: (
                <>
                  With discovery, I can find calendars based on{" "}
                  <b>artists, actors, players and so much more</b>. It makes it
                  so much easier for me to find what I'm looking for.
                </>
              ),
              src: User2,
            },
            {
              text: (
                <>
                  I really enjoy your product and how easy it is to use. Having
                  it for <b>sports schedules and my favorite TV shows</b> makes
                  life easier. I especially love with TV shows that I don't have
                  to hunt down each week whether it's a new episode or not.
                </>
              ),
              src: User3,
            },
            {
              text: (
                <>
                  Your sports calendars on my iPhone are awesome. In fact, I
                  sometimes think <b>they are magical</b>. Stanza changed the
                  Yankees home opener (the day before the actual event due to an
                  impending storm) before I even knew about the date change.
                </>
              ),
              src: User4,
            },
          ]}
        />

        <Divider sx={{ my: 2.5 }} />

        <Icon
          baseClassName="material-symbols-outlined"
          sx={{
            display: "block",
            margin: "0 auto",
            color: `${theme.palette.icon.primary}`,
            verticalAlign: "middle",
            fontSize: "3rem",
            mb: 1,
            fontWeight: 300,
          }}
        >
          rocket_launch
        </Icon>
        <Typography variant="title" sx={{ mb: 3 }}>
          {!isLifeTimeMember
            ? "Ready to put Stanza+ to the test?"
            : "Current Plan: Lifetime Membership"}
        </Typography>
        <Box display="flex" flexDirection="column" pl={3} gap={1} mb={3}>
          <Typography variant="body1" sx={{ mb: 1, textAlign: "left" }}>
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{
                color: `${theme.palette.icon.primary}`,
                verticalAlign: "bottom",
                mr: "10px",
                fontWeight: 300,
              }}
            >
              all_inclusive
            </Icon>
            Unlimited calendar subscriptions
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, textAlign: "left" }}>
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{
                color: `${theme.palette.icon.primary}`,
                verticalAlign: "bottom",
                mr: "10px",
                fontWeight: 300,
              }}
            >
              lock_open
            </Icon>
            Access to exclusive calendars
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, textAlign: "left" }}>
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{
                color: `${theme.palette.icon.primary}`,
                verticalAlign: "bottom",
                mr: "10px",
                fontWeight: 300,
              }}
            >
              alarm
            </Icon>
            Know when your calendar last updated
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, textAlign: "left" }}>
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{
                color: `${theme.palette.icon.primary}`,
                verticalAlign: "bottom",
                mr: "10px",
                fontWeight: 300,
              }}
            >
              folder_supervised
            </Icon>
            Create and share groups
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, textAlign: "left" }}>
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{
                color: `${theme.palette.icon.primary}`,
                verticalAlign: "bottom",
                mr: "10px",
                fontWeight: 300,
              }}
            >
              download
            </Icon>
            Download individual events
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, textAlign: "left" }}>
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{
                color: `${theme.palette.icon.primary}`,
                verticalAlign: "bottom",
                mr: "10px",
                fontWeight: 300,
              }}
            >
              support_agent
            </Icon>
            24/5 customer service
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, textAlign: "left" }}>
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{
                color: `${theme.palette.icon.primary}`,
                verticalAlign: "bottom",
                mr: "10px",
                fontWeight: 300,
              }}
            >
              insights
            </Icon>
            Personalized recommendations
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, textAlign: "left" }}>
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{
                color: `${theme.palette.icon.primary}`,
                verticalAlign: "bottom",
                mr: "10px",
                fontWeight: 300,
              }}
            >
              ad_units
            </Icon>
            Early access to mobile app
          </Typography>
          <Typography variant="body1" sx={{ textAlign: "left" }}>
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{
                color: `${theme.palette.icon.primary}`,
                verticalAlign: "bottom",
                mr: "10px",
                fontWeight: 300,
              }}
            >
              featured_seasonal_and_gifts
            </Icon>
            Early access to new features
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          sx={{ mt: 2 }}
          onClick={handleUpgradeClick}
        >
          {!isLifeTimeMember ? "Get 7 Days Free" : "Go home"}
        </Button>
        {!isLifeTimeMember && (
          <Typography
            variant="subtitle2"
            sx={{ pt: 0.5, margin: "auto", mb: 4 }}
          >
            Billed $0 today. No commitment, cancel anytime.
          </Typography>
        )}
      </Stack>
    </>
  );
};

export default ConsumerUpgradeContent;
